import type { EObject } from '../models/EObject';
import type { PatchParams } from './object-api';
import type { BaseParams, BodyParams, ClassParams, DelegationParams, ObjectParams } from '@sydle/ui-api';
import type { FetchResponse, RestService } from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';
import { DraftDelegationInterceptor } from './delegation/draft-delegation-interceptor';
import { DraftFeedbackInterceptor } from './feedback/draft-feedback-interceptor';

export type CreateDraftParams = BaseParams &
  ClassParams &
  Partial<ObjectParams> &
  Partial<DelegationParams> &
  Partial<BodyParams>;
export type GetDraftParams = BaseParams & ClassParams & Partial<DelegationParams> & ObjectParams & Partial<BodyParams>;
export type DeleteDraftParams = BaseParams & ClassParams & Partial<DelegationParams> & ObjectParams;
export type UpdateDraftParams = BaseParams &
  ClassParams &
  Partial<DelegationParams> &
  ObjectParams &
  Partial<BodyParams>;
export type PublishParams = BaseParams &
  ClassParams &
  Partial<DelegationParams> &
  ObjectParams & { ignoreRevision?: boolean };

export enum DraftMethods {
  CREATE = '_createDraft',
  GET = '_getDraft',
  MULTIPLE_GET = '_getDrafts',
  PUBLISH = '_publish',
  PATCH = '_patchDraft',
  UPDATE = '_updateDraft',
}

/**
 * API de rascunhos.
 * @author gabriel.carmo.
 */
export class DraftAPI {
  private restService: RestService;

  constructor(restService: RestService, enableDelegation?: boolean, disableFieldLocalizedFeedbacks?: boolean) {
    this.restService = restService;
    if (enableDelegation) {
      this.restService.registerInterceptor(new DraftDelegationInterceptor());
    }

    if (!disableFieldLocalizedFeedbacks) {
      this.restService.registerInterceptor(new DraftFeedbackInterceptor());
    }
  }

  create(args: CreateDraftParams): Promise<FetchResponse<EObject>> {
    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: DraftMethods.CREATE,
      queryParams: { _translate: false },
    });
    return this.restService.post(requestParams);
  }

  get(args: GetDraftParams): Promise<FetchResponse<EObject>> {
    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: DraftMethods.GET,
      queryParams: { _translate: false },
    });

    return this.restService.post(requestParams);
  }

  delete(args: DeleteDraftParams): Promise<FetchResponse<any>> {
    const requestParams = buildRequestParams({ ...args, methodIdentifier: '_deleteDraft' });

    return this.restService.post(requestParams);
  }

  update(args: UpdateDraftParams): Promise<FetchResponse<EObject>> {
    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: DraftMethods.UPDATE,
      queryParams: { _translate: false },
    });

    return this.restService.post(requestParams);
  }

  patch(args: PatchParams): Promise<FetchResponse<EObject>> {
    const { operations, ignoreResponseBody } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: DraftMethods.PATCH,
      body: operations,
      queryParams: {
        ...args.queryParams,
        ignoreResponseBody,
        _translate: false,
      },
    });

    return this.restService.post(requestParams);
  }

  publish(args: PublishParams): Promise<FetchResponse<EObject>> {
    const body = args.ignoreRevision ? { _revision: null } : null;
    const requestParams = buildRequestParams({ ...args, methodIdentifier: DraftMethods.PUBLISH, body });

    return this.restService.post(requestParams);
  }
}
